import React, { useEffect } from 'react';
import { SlDropdownMenu, SlDropdownMenuProps } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { Tooltip, TooltipProps } from '@mui/material';
import color from 'src/utils/colors';

interface StyledTooltipProps extends Omit<TooltipProps, 'children'> {
  children: React.ReactElement;
}

const StyledTooltip: React.FC<StyledTooltipProps> = ({ children, ...props }) => {
  return (
    <Tooltip
      title={props.title}
      enterDelay={300}
      followCursor
      disableInteractive
      enterNextDelay={0}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            fontSize: '12px',
            padding: '4px 8px',
            backdropFilter: 'blur(4px)',
            '& .MuiTooltip-arrow': {
              color: 'rgba(0, 0, 0, 0.6)'
            }
          }
        }
      }}
      {...props}
    >
      {children}
    </Tooltip>
  );
};

export const DropdownMenu = (props: SlDropdownMenuProps) => {
  const { disabled, stacklineButtonProps, ...restProps } = props;

  return (
    <SlDropdownMenu
      iconStyle={{ marginLeft: '10px' }}
      disabled={disabled}
      // Hide chevron icon when disabled (readOnly mode)
      customEndIcon={disabled ? null : undefined}
      stacklineButtonProps={{
        ...stacklineButtonProps,
        buttonSx: {
          '& .MuiTypography-root': {
            fontWeight: '400 !important',
            maxWidth: '200px',
            overflow: 'hidden'
          },
          ...stacklineButtonProps?.buttonSx,
          // Add paddingRight when disabled (no chevron) - must come last to override other paddingRight values
          ...(disabled && { paddingRight: '12px' })
        }
      }}
      menuStyles={{
        maxHeight: '288px',
        border: '1px solid #E8E8ED',
        '& .MuiMenuItem-root': {
          paddingTop: '8px',
          paddingBottom: '8px'
        },
        '& .MuiTypography-root': {
          fontWeight: '400 !important'
        },
        '&::-webkit-scrollbar': {
          width: '5px'
        },
        '&::-webkit-scrollbar-thumb': {
          background: color.blueGrey,
          borderRadius: '20px'
        }
      }}
      {...restProps}
    />
  );
};

/**
 * @param name: name of the field in the form
 * name could be a nested path, e.g. "settings.voice.type" or simple as "profileName"
 * name is a strictly typed string, comming from available name/path of the form <T>
 */
type RHFLabelledDropdownProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  options: SlDropdownMenuOption[];
  defaultValue?: string;
  showTooltip?: boolean;
} & Omit<SlDropdownMenuProps, 'selectedId' | 'onChange'>;

export function LabelledDropdown<T extends FieldValues>({
  name,
  label,
  options,
  defaultValue,
  showTooltip = false,
  ...dropdownProps
}: RHFLabelledDropdownProps<T>) {
  const { control, setValue, getValues } = useFormContext<T>();

  // Initialize form value with defaultValue if current value is empty
  useEffect(() => {
    if (defaultValue) {
      const currentValue = getValues(name);
      if (!currentValue) {
        setValue(name, defaultValue as any, { shouldValidate: true });
      }
    }
  }, [defaultValue, name, setValue, getValues]);

  return (
    <Controller
      control={control}
      render={({ field: { value, onChange, ...restField } }) => {
        // Find the selected option to get its label for the tooltip
        const selectedOption = options.find((option) => option.id === value);
        const selectedLabel = selectedOption?.label || '';

        return (
          <Flex flexDirection="column" gap={8}>
            <Text
              variant="h7"
              sx={{
                fontWeight: 500
              }}
            >
              {label}
            </Text>
            {showTooltip ? (
              <StyledTooltip title={selectedLabel}>
                <div>
                  <DropdownMenu
                    defaultValue={defaultValue}
                    options={options}
                    onChange={(option: SlDropdownMenuOption) => {
                      setValue(name, option.id as any, { shouldValidate: true });
                      // field.onChange() does not work in this case
                    }}
                    selectedId={(value as string) || (defaultValue as string)}
                    {...dropdownProps}
                    {...restField}
                  />
                </div>
              </StyledTooltip>
            ) : (
              <DropdownMenu
                defaultValue={defaultValue}
                options={options}
                onChange={(option: SlDropdownMenuOption) => {
                  setValue(name, option.id as any, { shouldValidate: true });
                  // field.onChange() does not work in this case
                }}
                selectedId={(value as string) || (defaultValue as string)}
                {...dropdownProps}
                {...restField}
              />
            )}
          </Flex>
        );
      }}
      name={name}
    />
  );
}

type RHFControlledDropdownProps<T extends FieldValues> = Pick<
  SlDropdownMenuProps,
  'stacklineButtonProps' | 'menuStyles'
> & {
  name: Path<T>;
  options: SlDropdownMenuOption[];
  defaultValue?: string;
  defaultLabel?: string;
  showTooltip?: boolean;
};

export const ControlledDropdownMenu = <T extends FieldValues>({
  name,
  options,
  defaultValue,
  defaultLabel,
  showTooltip = false,
  ...restProps
}: RHFControlledDropdownProps<T>) => {
  const { control, setValue, getValues } = useFormContext<T>();

  // Initialize form value with defaultValue if current value is empty
  useEffect(() => {
    if (defaultValue) {
      const currentValue = getValues(name);
      if (!currentValue) {
        setValue(name, defaultValue as any, { shouldValidate: true });
      }
    }
  }, [defaultValue, name, setValue, getValues]);

  return (
    <Controller
      control={control}
      render={({ field: { value, onChange, ...restField } }) => {
        // Find the selected option to get its label for the tooltip
        const selectedOption = options.find((option) => option.id === value);
        const selectedLabel = selectedOption?.label || '';

        return showTooltip ? (
          <StyledTooltip title={selectedLabel}>
            <div>
              <DropdownMenu
                defaultValue={defaultValue}
                defaultLabel={defaultLabel}
                options={options}
                onChange={(option: SlDropdownMenuOption) => {
                  setValue(name, option.id as any, { shouldValidate: true });
                }}
                selectedId={(value as string) || defaultValue}
                {...restProps}
                {...restField}
              />
            </div>
          </StyledTooltip>
        ) : (
          <DropdownMenu
            defaultValue={defaultValue}
            defaultLabel={defaultLabel}
            options={options}
            onChange={(option: SlDropdownMenuOption) => {
              setValue(name, option.id as any, { shouldValidate: true });
            }}
            selectedId={(value as string) || defaultValue}
            {...restProps}
            {...restField}
          />
        );
      }}
      name={name}
    />
  );
};
